/**
 * The tray of a boggle and the visible rules of a word on it, mirrored from
 * `backend/apps/puzzletypes/boggle/tray.py`. The dictionary is not here and never will be:
 * whether a word exists is only known to the server, at submission.
 */
import type { Cell, Face, WordIssue } from "./types";

export const NEIGHBOURS: Cell[] = [
  [-1, -1],
  [-1, 0],
  [-1, 1],
  [0, -1],
  [0, 1],
  [1, -1],
  [1, 0],
  [1, 1],
];

/** Mirrors the server's `normalize_word`: accents removed, surrounding spaces trimmed, uppercase. */
export function normalizeWord(word: string): string {
  return word
    .trim()
    .normalize("NFD")
    .replace(/\p{Mn}/gu, "")
    .normalize("NFC")
    .toUpperCase();
}

export function isFace(face: unknown): face is Face {
  return face === "QU" || (typeof face === "string" && /^[A-Z]$/.test(face));
}

/** The faces as a grid, or null when they do not make a `size` × `size` tray. */
export function gridOf(raw: unknown, size: number): Face[][] | null {
  if (!Array.isArray(raw) || raw.length !== size) return null;
  const rows: Face[][] = [];
  for (const row of raw) {
    if (!Array.isArray(row) || row.length !== size || !row.every(isFace)) return null;
    rows.push([...row]);
  }
  return rows;
}

export function adjacent(a: Cell, b: Cell): boolean {
  const dr = Math.abs(a[0] - b[0]);
  const dc = Math.abs(a[1] - b[1]);
  return (dr !== 0 || dc !== 0) && dr <= 1 && dc <= 1;
}

const has = (path: Cell[], [r, c]: Cell) => path.some(([pr, pc]) => pr === r && pc === c);

/** One path spelling `word` on the tray, or null. Same search order as the server. */
export function trace(grid: Face[][], word: string): Cell[] | null {
  const size = grid.length;
  if (!word) return null;
  const extend = (path: Cell[], at: number): Cell[] | null => {
    if (at === word.length) return path;
    const [r, c] = path[path.length - 1];
    for (const [dr, dc] of NEIGHBOURS) {
      const next: Cell = [r + dr, c + dc];
      if (next[0] < 0 || next[0] >= size || next[1] < 0 || next[1] >= size || has(path, next))
        continue;
      const face = grid[next[0]][next[1]];
      if (word.startsWith(face, at)) {
        const found = extend([...path, next], at + face.length);
        if (found) return found;
      }
    }
    return null;
  };
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const face = grid[r][c];
      if (word.startsWith(face)) {
        const found = extend([[r, c]], face.length);
        if (found) return found;
      }
    }
  }
  return null;
}

/** The word a path of dice spells. */
export function spell(grid: Face[][], path: Cell[]): string {
  return path.map(([r, c]) => grid[r]?.[c] ?? "").join("");
}

/** The standard table: 3–4 letters 1, 5 → 2, 6 → 3, 7 → 5, 8 and more → 11. */
export function points(word: string): number {
  const length = word.length;
  if (length <= 4) return 1;
  return ({ 5: 2, 6: 3, 7: 5 } as Record<number, number>)[length] ?? 11;
}

/** Why `word` (already normalised) breaks a visible rule, or null when it keeps them all. */
export function wordIssue(grid: Face[][], minLength: number, word: string): WordIssue | null {
  if (word.length < minLength) return "too_short";
  if (!/^[A-Z]+$/.test(word) || trace(grid, word) === null) return "not_on_grid";
  return null;
}
